import type { StaticImageData } from 'next/image'
import type { IconName } from '@/components/ui/Icon'
import canvas from './shots/canvas.png'
import library from './shots/library.png'
import packList from './shots/pack-list.png'
import inspectorReach from './shots/inspector-reach.png'
import runSheet from './shots/run-sheet.png'
import runSheetPhone from './shots/run-sheet-phone.png'
import power from './shots/power.png'

/**
 * Everything the landing page says, as data.
 *
 * Copy lives here rather than in JSX so a wording change is a one-line edit
 * and never touches layout. The screenshots are captured by
 * `scripts/capture-landing.mjs` from the sample project; re-run it when the
 * UI changes and the imports below pick up the new files.
 *
 * Tone: plain and short. The reader is a technical director or a podcaster
 * deciding whether this is worth an account, not someone who needs selling.
 */

/** `narrow` is for a tall panel crop (palette, inspector) that must not fill a column; `phone` for a phone viewport. */
export type Shot = { image: StaticImageData; alt: string; phone?: boolean; narrow?: boolean }

export const HERO = {
  title: 'Plan the cabling before the truck arrives.',
  intro:
    'ROUTR is a signal-flow planner for live production. Pick devices from a library of real gear, patch them on a canvas, and get a run sheet, cable pack list, schematic and power estimate you can hand to the crew.',
  note: 'Free to use. Works in the browser.',
  shot: {
    image: canvas,
    alt: 'The ROUTR canvas showing the sample four-camera rig: cameras, switcher, recorder, multiview, audio console, network switch and rack PDU, joined by video, audio, network and power cables in separate colours.',
  } satisfies Shot,
}

export const PILLARS: Array<{ icon: IconName; title: string; body: string }> = [
  {
    icon: 'equipment',
    title: 'A library of real equipment',
    body: 'Over 350 devices from 30 manufacturers, with the ports they actually have: SDI, HDMI, XLR, USB-C, Dante, Ethernet, IEC. Add your own when something is missing.',
  },
  {
    icon: 'cable',
    title: 'A canvas for patching',
    body: 'Drag devices on and connect output to input. Video, audio, network and power cables keep their own colour and their own lane, so a bundle reads as a bundle.',
  },
  {
    icon: 'sheet',
    title: 'Paperwork that writes itself',
    body: 'The run sheet, cable pack list, schematic and power plan come straight from the canvas. On screen, on a phone at the venue, or as CSV, PNG and PDF.',
  },
]

export const STEPS: Array<{ title: string; body: string; shots: Shot[] }> = [
  {
    title: 'Start with a rig',
    body: 'New project opens a picker: a two-host podcast, a video podcast, a single-camera stream, or a four-camera live rig. Each one arrives fully patched, from real gear, ready to change. Or start blank and pull equipment in as you go.',
    shots: [
      {
        image: library,
        alt: 'The equipment library panel on the canvas, listing AVerMedia, AVMatrix and Blackmagic cameras with a search box and type and brand filters.',
        narrow: true,
      },
    ],
  },
  {
    title: 'Patch port to port',
    body: 'Click a device to place it, then drag from an output to an input. A cable only lands on a port that can take it, and a second cable into the same input is refused.',
    shots: [
      {
        image: canvas,
        alt: 'The canvas with the sample rig laid out in four columns and every cable routed between named ports.',
      },
    ],
  },
  {
    title: 'Let it check your work',
    body: 'A 6K camera into an HD switcher. A run that will quietly drop to 1080p. A coax pulled further than it can carry. ROUTR flags each one before load-in, not on the day.',
    shots: [
      {
        image: packList,
        alt: 'The cable pack list panel over the canvas for a 2160p50 show, warning that 20 runs have no length yet and listing each one by cable type and connector.',
      },
      {
        image: inspectorReach,
        alt: 'The cable inspector with a 150 metre length entered and a red warning that it is past the 45 metres this cable manages at 12G-SDI.',
        narrow: true,
      },
    ],
  },
  {
    title: 'Print the run sheet',
    body: 'Every cable gets a label, a from, a to, a length and a type. Open it on a phone at the venue, or export CSV and an A4 PDF for the rigging crew.',
    shots: [
      {
        image: runSheet,
        alt: 'The run sheet listing every cable in the sample rig with its label, endpoints, connector, length and signal.',
      },
      {
        image: runSheetPhone,
        alt: 'The same run sheet on a phone, one cable per card.',
        phone: true,
      },
    ],
  },
  {
    title: 'Size the power',
    body: 'The power calculator reads the gear off the canvas and tells you what to ask for, in words the hire company uses: any set will do, or you need a 20 kVA one.',
    shots: [
      {
        image: power,
        alt: 'The power calculator for the sample rig showing the total draw with headroom and the generator size to hire.',
      },
    ],
  },
]

export const AUDIENCES: Array<{ icon: IconName; title: string; body: string }> = [
  {
    icon: 'broadcast',
    title: 'Technical directors and streaming producers',
    body: 'Multi-camera OB and studio work: SDI formats, Dante, control networks, rack power, and the paperwork a freelance crew expects to be handed.',
  },
  {
    icon: 'headset',
    title: 'Podcasters and small studios',
    body: 'Entry-tier gear is in the catalogue, from Elgato and Atomos to RØDE and Mevo, so a two-camera room is as easy to plan as a truck. Simple mode hides what you do not need.',
  },
  {
    icon: 'user',
    title: 'Companies with a shared kit',
    body: 'A workspace is your studio, your show or your company. Work in it alone, or invite people, keep the inventory in one place and see which unit is on which show.',
  },
]

export const FEATURES: Array<{ icon: IconName; title: string; body: string }> = [
  { icon: 'equipment', title: 'Equipment library', body: 'Over 350 devices, 30 manufacturers, real port lists. Add your own gear.' },
  { icon: 'cable', title: 'Patching canvas', body: 'Port-to-port cables in four colours, with lanes so runs stay readable.' },
  { icon: 'eye', title: 'Automatic checks', body: 'Format mismatches, runs that lose picture, cables past their reach.' },
  { icon: 'sheet', title: 'Run sheet and pack list', body: 'Phone page, CSV and PDF, straight from the canvas.' },
  { icon: 'image', title: 'Schematic export', body: 'PNG or PDF of the whole system, with a title block and legend.' },
  { icon: 'plug', title: 'Power calculator', body: 'Total draw with headroom, and the generator size to hire.' },
]

export function faq(signupsOpen: boolean): Array<{ q: string; a: string }> {
  return [
    {
      q: 'Is it free?',
      a: signupsOpen
        ? 'Yes. Sign-ups are open and there is no paid tier.'
        : 'Yes. Sign-ups are closed at the moment; ask a workspace owner for an invite link.',
    },
    {
      q: 'What gear is in the catalogue?',
      a: 'Over 350 devices from 30 manufacturers, with their real inputs and outputs: Blackmagic Design, AJA, Sony, Panasonic, Canon, Magewell, AVerMedia, Hollyland, Atomos, Elgato, RØDE, Shure, Focusrite, TASCAM, Zoom, Yamaha, Cisco, APC and more. Entries checked against a spec sheet are marked as verified.',
    },
    {
      q: 'Can I add my own gear?',
      a: 'Yes. Any device can be added to your workspace with its ports and shared with everyone in it. It stays private to your company.',
    },
    {
      q: 'Does it work on a phone?',
      a: 'The canvas wants a laptop. The run sheet, pack list and power answer are made for a phone at the venue, and the whole app installs as a web app.',
    },
  ]
}

export const FOOTER = {
  credit: 'Built in Indonesia by Mfalme Studio.',
}
